import type { TFunction } from 'i18next'

/**
 * Die **Fokuszeit mit Einheit** — „9s", „9m 53s", „1h 20m".
 *
 * Getrennt von `formatFocusClock` (dem Chip des Fokus-Fensters): Der Chip tickt im
 * Sekundentakt und braucht die stellenfeste Uhr. Überall sonst wird gelesen,
 * nicht mitgezählt — und `0:09m` zwingt zum Umrechnen, bevor man begreift, dass
 * neun Sekunden gemeint sind (gemeldet 2026-09-18).
 *
 * **Die Einheit hängt direkt an der Zahl**, jede für sich. Das ist der ganze
 * Unterschied: Eine Zahl mit Doppelpunkt kann eine Uhrzeit sein, „9m 53s" nicht.
 * Kurz wie `formatDuration` der veranschlagten Dauer („10m", „1h 30m") — die
 * beiden stehen in der **Dauer-Marke** direkt nebeneinander und dürfen nicht in
 * zwei Sprachen reden.
 *
 * **Sekunden nur unterhalb einer Stunde.** Darüber sind sie Lärm — wer zwei
 * Stunden gearbeitet hat, liest nicht nach, ob es 7243 waren. Darunter tragen
 * sie: Ob 9:53 oder 10:00 verbraucht sind, ist der Unterschied zwischen „knapp
 * dran" und „drüber".
 *
 * **Glatte Werte verlieren den Rest**: „9m", nicht „9m 0s".
 *
 * Das Glossar-Wort **fokussiert** steht NICHT hier, sondern im Satz der
 * jeweiligen Stelle: „1h 20m fokussiert" ist ein Satz, „1h 20m" eine Angabe.
 * So kann jede Stelle ihren eigenen Satz bauen.
 *
 * @example
 * formatFocusDuration(9, t)      // „9s"
 * formatFocusDuration(593, t)    // „9m 53s"
 * formatFocusDuration(4800, t)   // „1h 20m"
 */
export function formatFocusDuration(seconds: number, t: TFunction): string {
  // Negative Sekunden gibt es nicht — ein Abschnitt, der rückwärts lief, ist
  // ein Fehler anderswo und soll hier nicht als „-3 Min" verkleidet werden.
  const total = Math.max(0, Math.trunc(seconds))

  if (total < 60) return t('focus.duration.seconds', { count: total })

  if (total < 3600) {
    const minutes = Math.floor(total / 60)
    const rest = total % 60
    return rest === 0
      ? t('focus.duration.minutes', { count: minutes })
      : t('focus.duration.minutesSeconds', { minutes, seconds: rest })
  }

  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  return minutes === 0
    ? t('focus.duration.hours', { count: hours })
    : t('focus.duration.hoursMinutes', { hours, minutes })
}

/**
 * Die Fokuszeit **gegen die veranschlagte Dauer** — „9s von 10m".
 *
 * Erst nebeneinander sagen die beiden Zahlen etwas: Eine gelaufene Zeit allein
 * beantwortet nicht, ob sie reicht. Steht keine Dauer fest, bleibt die Angabe
 * allein stehen — ein „von" ins Leere wäre schlimmer als keins.
 *
 * Beide Seiten in derselben Schreibweise, damit man sie vergleichen kann, ohne
 * zwei Sprachen zu lesen.
 *
 * @example
 * formatFocusAgainstEstimate(9, 10, t)       // „9s von 10m"
 * formatFocusAgainstEstimate(593, null, t)   // „9m 53s"
 */
export function formatFocusAgainstEstimate(
  seconds: number,
  estimateMinutes: number | null | undefined,
  t: TFunction,
): string {
  const spent = formatFocusDuration(seconds, t)
  if (typeof estimateMinutes !== 'number' || estimateMinutes <= 0) return spent
  return t('focus.duration.ofEstimate', {
    spent,
    estimate: formatFocusDuration(estimateMinutes * 60, t),
  })
}

/** Ob die Fokuszeit die veranschlagte Dauer überschritten hat. */
export function isOverEstimate(seconds: number, estimateMinutes: number | null | undefined): boolean {
  return typeof estimateMinutes === 'number' && estimateMinutes > 0 && seconds > estimateMinutes * 60
}
